import { compact, join, orderBy } from 'es-toolkit/compat';

import { JobPreset } from 'interfaces/jobPreset';

export const CALCULATE_FEATURES_PRESET_GROUP = {
  currentStudy: 'Current study',
  global: 'Global',
  noStudy: 'No study',
} as const;

export interface CalculateFeaturesPresetGroupContext {
  studyId?: string;
  labId?: string;
}

const getThisLabGroup = (studyName?: string) => `This lab · ${studyName || 'Untitled study'}`;

const getOtherLabGroup = (labName?: string, studyName?: string) =>
  `${labName || 'Other lab'} · ${studyName || 'Untitled study'}`;

export const getCalculateFeaturesPresetGroup = (
  preset: Pick<JobPreset, 'isGlobal' | 'sourceStudyId' | 'sourceStudyName' | 'labId' | 'labName'>,
  context: CalculateFeaturesPresetGroupContext
): string => {
  if (preset.isGlobal) {
    return CALCULATE_FEATURES_PRESET_GROUP.global;
  }

  if (context.studyId && preset.sourceStudyId === context.studyId) {
    return CALCULATE_FEATURES_PRESET_GROUP.currentStudy;
  }

  if (context.labId && preset.labId && preset.labId === context.labId) {
    return getThisLabGroup(preset.sourceStudyName);
  }

  if (preset.labId) {
    return getOtherLabGroup(preset.labName, preset.sourceStudyName);
  }

  return CALCULATE_FEATURES_PRESET_GROUP.noStudy;
};

const getGroupSortRank = (group: string) => {
  if (group === CALCULATE_FEATURES_PRESET_GROUP.currentStudy) {
    return 0;
  }
  if (group.startsWith('This lab · ')) {
    return 1;
  }
  if (group === CALCULATE_FEATURES_PRESET_GROUP.global) {
    return 3;
  }
  if (group === CALCULATE_FEATURES_PRESET_GROUP.noStudy) {
    return 4;
  }
  return 2;
};

export const sortCalculateFeaturesPresetsForAutocomplete = (
  presets: JobPreset[],
  context: CalculateFeaturesPresetGroupContext
): JobPreset[] =>
  orderBy(
    presets,
    [
      (preset) => getGroupSortRank(getCalculateFeaturesPresetGroup(preset, context)),
      (preset) => getCalculateFeaturesPresetGroup(preset, context).toLowerCase(),
      (preset) => (preset.name || '').toLowerCase(),
    ],
    ['asc', 'asc', 'asc']
  );

export const getCalculateFeaturesPresetFilterText = (preset: JobPreset) =>
  join(compact([preset.name, preset.sourceStudyName, preset.labName, ...(preset.stains || [])]), ' ');
